import type {
    SpeechEngine,
    SpeechRequest,
    SpeechVoiceOption
} from "./SpeechEngine.js";
import { splitSpeechText } from "./speechLanguage.js";

const CANCELLATION_ERRORS = new Set(["canceled", "interrupted"]);
const NATURAL_VOICE_MARKERS = /(?:natural|neural|enhanced|premium|google)/iu;
const ROBOTIC_VOICE_MARKERS = /(?:desktop|compact|espeak|festival)/iu;

interface SpeechSegment {
    text: string;
    offset: number;
}

function splitAtSemanticBreaks(
    text: string,
    breaks: readonly number[] = []
): SpeechSegment[] {
    const boundaries = [
        0,
        ...new Set(
            breaks.filter(value =>
                Number.isInteger(value)
                && value > 0
                && value < text.length
            )
        ),
        text.length
    ].sort(
        (left, right) =>
            left - right
    );
    const segments: SpeechSegment[] = [];

    for (
        let index = 0;
        index < boundaries.length - 1;
        index += 1
    ) {
        const start = boundaries[index] ?? 0;
        const end = boundaries[index + 1] ?? text.length;
        const raw = text.slice(
            start,
            end
        );
        const leadingWhitespace =
            raw.match(/^\s*/u)?.[0].length
            ?? 0;
        const clean = raw.trim();

        if (clean) {
            segments.push({
                text: clean,
                offset: start + leadingWhitespace
            });
        }
    }

    return segments;
}

class BrowserSpeechEngine implements SpeechEngine {
    private generation = 0;

    isSupported(): boolean {
        return typeof window !== "undefined"
            && "speechSynthesis" in window
            && typeof SpeechSynthesisUtterance !== "undefined";
    }

    async speak(request: SpeechRequest): Promise<void> {
        if (!this.isSupported()) {
            throw new Error("Speech synthesis is not supported by this browser.");
        }

        this.stop();
        const generation = this.generation;

        for (
            const segment of splitAtSemanticBreaks(
                request.text,
                request.segmentBreaks
            )
        ) {
            let searchStart = 0;

            for (const chunk of splitSpeechText(segment.text)) {
                if (generation !== this.generation) return;

                const foundOffset =
                    segment.text.indexOf(
                        chunk,
                        searchStart
                    );
                const localOffset =
                    foundOffset >= 0
                        ? foundOffset
                        : searchStart;
                const chunkOffset =
                    segment.offset
                    + localOffset;

                searchStart =
                    localOffset
                    + chunk.length;

                await this.speakChunk(
                    chunk,
                    request,
                    generation,
                    chunkOffset
                );
            }
        }
    }

    listVoices(): SpeechVoiceOption[] {
        if (!this.isSupported()) return [];

        return window.speechSynthesis.getVoices().map(voice => ({
            id: voice.voiceURI,
            name: voice.name,
            lang: voice.lang,
            localService: voice.localService,
            quality: NATURAL_VOICE_MARKERS.test(voice.name)
                ? "natural"
                : "standard"
        }));
    }

    stop(): void {
        this.generation += 1;
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.cancel();
        }
    }

    pause(): void {
        if (this.isSupported() && window.speechSynthesis.speaking) {
            window.speechSynthesis.pause();
        }
    }

    resume(): void {
        if (this.isSupported() && window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
        }
    }

    private speakChunk(
        text: string,
        request: SpeechRequest,
        generation: number,
        chunkOffset: number
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = request.lang;
            utterance.rate = request.rate ?? 1;
            utterance.pitch = request.pitch ?? 1;
            utterance.volume = request.volume ?? 1;
            utterance.voice = this.findVoice(request.lang, request.voiceId) ?? null;
            utterance.onboundary = event => {
                if (generation !== this.generation) return;
                request.onBoundary?.({
                    charIndex: chunkOffset + event.charIndex,
                    charLength: event.charLength,
                    name: event.name
                });
            };
            utterance.onend = () => resolve();
            utterance.onerror = event => {
                if (generation !== this.generation || CANCELLATION_ERRORS.has(event.error)) {
                    resolve();
                    return;
                }
                reject(new Error(`Speech synthesis failed: ${event.error || "unknown error"}`));
            };
            window.speechSynthesis.speak(utterance);
        });
    }

    private findVoice(
        locale: string,
        preferredVoiceId?: string
    ): SpeechSynthesisVoice | undefined {
        const normalized = locale.toLowerCase();
        const base = normalized.split("-")[0] ?? normalized;
        const voices = window.speechSynthesis.getVoices();

        const preferred = voices.find(voice => voice.voiceURI === preferredVoiceId);
        if (preferred && preferred.lang.toLowerCase().startsWith(base)) {
            return preferred;
        }

        return voices
            .filter(voice => voice.lang.toLowerCase().startsWith(base))
            .sort((left, right) => (
                this.scoreVoice(right, normalized)
                - this.scoreVoice(left, normalized)
            ))[0];
    }

    private scoreVoice(voice: SpeechSynthesisVoice, locale: string): number {
        const normalizedLanguage = voice.lang.toLowerCase();
        let score = normalizedLanguage === locale ? 100 : 50;

        if (NATURAL_VOICE_MARKERS.test(voice.name)) score += 40;
        if (ROBOTIC_VOICE_MARKERS.test(voice.name)) score -= 30;
        if (voice.default) score += 5;

        return score;
    }
}

export { BrowserSpeechEngine };
